import type {
  CheckoutDeliveryOption,
  IDeliveryAddressesResponse,
  IDeliveryAddressView,
  IHandleGetUserDeliveryAddressesParams,
  HandleAuthStateChangeParams,
  HandleCheckoutParams,
  HandleModalFocusParams,
  RefreshAddressesParams,
  ICreateDeliveryAddressFormData,
  ICreateDeliveryAddressResponse,
  IHandleCreateDeliveryAddressParams,
  IHandleSetDefaultDeliveryAddressParams,
  ISetDefaultDeliveryAddressResponse,
  IHandleDeleteDeliveryAddressParams,
  IHandleOpenDeleteDeliveryAddressParams,
  HandleSetDefaultFlowParams,
  HandleDeleteAndRefreshParams,
  HandleOpenAddParams,
  HandleCloseAddParams,
  HandleCreatedParams,
  IDeliveryCarrier,
  GetDeliveryCarriersResponse,
  IHandleGetCarriersParams,
  ISelectCarrierParams,
  IDefaultCarrierParams,
} from '@model/DeliveryAddress.model';
import type { Dispatch, SetStateAction } from 'react';
import { AxiosCalls } from './Functions/AxiosCalls';
import { handleDefaultApiErrors } from 'Utils/Global/apiErrorHandling';

export class DeliveryAddressService {
  async handleGetUserDeliveryAddresses({
    setLoading,
    setApiError,
    setItems,
    setTotal,
    token,
    onAuthError,
    onForbidden,
  }: IHandleGetUserDeliveryAddressesParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Get<IDeliveryAddressesResponse>(
        '/delivery-address/getAddresses',
        token
      );

      const payload = resp?.data ?? {};
      const itemsRaw: IDeliveryAddressView[] = payload?.data?.items ?? [];

      setItems(itemsRaw);

      const total = typeof payload?.meta?.total === 'number' ? payload.meta.total : itemsRaw.length;

      if (setTotal) setTotal(total);
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
      setItems([]);
      if (setTotal) setTotal(0);
    } finally {
      setLoading(false);
    }
  }
  async handleAuthStateChange({
    token,
    clearAddresses,
    resetSelection,
    loadAddresses,
    onAuthError,
    onForbidden,

    setOpenAddModal,
    setOpenDeleteModal,
    setDeleteError,
  }: HandleAuthStateChangeParams): Promise<void> {
    const t = String(token ?? '').trim();

    if (!t) {
      clearAddresses();
      resetSelection();
      setOpenAddModal(false);
      setOpenDeleteModal(false);
      setDeleteError(null);
      return;
    }

    await loadAddresses({
      token: t,
      onAuthError,
      onForbidden,
    });
  }

  buildCheckoutOptions(addresses: IDeliveryAddressView[]): CheckoutDeliveryOption[] {
    return (addresses ?? []).map((a) => ({
      value: a.id,
      label: a.addressLabel,
      description: `${a.street}, ${a.houseNumber} • ${a.city}, ${a.region}`,
    }));
  }

  computeSelectedId(addresses: IDeliveryAddressView[], currentSelectedId: string): string {
    const list = addresses ?? [];
    const current = String(currentSelectedId ?? '');

    if (!list.length) return '';

    const stillExists = current && list.some((a) => a.id === current);
    if (stillExists) return current;

    return list.find((a) => a.isDefaultAddress)?.id ?? list[0]?.id ?? '';
  }
  computeSelectedAddress(params: {
    addresses: IDeliveryAddressView[];
    selectedAddress: IDeliveryAddressView | null;
    setSelectedAddress: (a: IDeliveryAddressView | null) => void;
  }): IDeliveryAddressView | null {
    const { addresses, selectedAddress, setSelectedAddress } = params;

    const list = addresses ?? [];
    if (!list.length) {
      if (selectedAddress) setSelectedAddress(null);
      return null;
    }

    const currentId = String(selectedAddress?.id ?? '');

    const stillExists = currentId && list.some((a) => a.id === currentId);

    const nextId = stillExists
      ? currentId
      : (list.find((a) => a.isDefaultAddress)?.id ?? list[0]?.id ?? '');

    const resolved =
      list.find((a) => a.id === nextId) ?? list.find((a) => a.isDefaultAddress) ?? list[0] ?? null;

    if (resolved?.id !== selectedAddress?.id) {
      setSelectedAddress(resolved);
    }

    return resolved;
  }

  async refreshAddresses({
    token,
    loadAddresses,
    onAuthError,
    onForbidden,
    resetSelection,
  }: RefreshAddressesParams): Promise<void> {
    if (!token) return;

    await loadAddresses({
      token,
      onAuthError,
      onForbidden,
    });

    resetSelection();
  }

  handleCheckout({ disableCheckout, onProceed }: HandleCheckoutParams): void {
    if (disableCheckout) return;
    onProceed();
  }

  handleModalFocus({ open, dialogRef, timeoutRef }: HandleModalFocusParams): () => void {
    if (open) {
      timeoutRef.current = window.setTimeout(() => {
        dialogRef.current?.focus();
      }, 0);
    }

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }

  async handleCreateDeliveryAddress({
    data,
    setLoading,
    setApiError,
    token,
    reset,
    onAuthError,
    onForbidden,
  }: IHandleCreateDeliveryAddressParams): Promise<string | null> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Post<
        ICreateDeliveryAddressResponse,
        ICreateDeliveryAddressFormData
      >('/delivery-address/add', data, token);

      if (reset) reset();

      return resp?.data?.message ?? 'Address created successfully.';
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }

  async handleSetDefaultDeliveryAddress({
    addressId,
    token,
    setLoading,
    setApiError,
    onAuthError,
    onForbidden,
  }: IHandleSetDefaultDeliveryAddressParams): Promise<string | null> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Put<ISetDefaultDeliveryAddressResponse, {}>(
        `/delivery-address/setDefault/${encodeURIComponent(addressId)}`,
        {},
        token
      );

      return resp?.data?.message ?? 'Default address updated successfully.';
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }

  async handleSetDefaultFlow({
    authToken,
    selectedAddress,
    setLoading,
    setApiError,
    clearError,
    onAuthError,
    onForbidden,
    refreshAddresses,
  }: HandleSetDefaultFlowParams): Promise<void> {
    if (!authToken) return;
    if (!selectedAddress || selectedAddress.isDefaultAddress) return;

    clearError();

    const msg = await this.handleSetDefaultDeliveryAddress({
      addressId: selectedAddress.id,
      token: authToken,
      setLoading,
      setApiError,
      onAuthError,
      onForbidden,
    });

    if (msg) {
      await refreshAddresses();
    }
  }

  async handleDeleteDeliveryAddress({
    addressId,
    token,
    selectedAddress,
    setLoading,
    setApiError,
    onAuthError,
    onForbidden,
  }: IHandleDeleteDeliveryAddressParams): Promise<boolean> {
    if (!token) return false;
    if (!selectedAddress) return false;
    if (selectedAddress.isDefaultAddress) return false;

    setLoading(true);
    setApiError(null);

    try {
      await AxiosCalls.Delete<void>(
        `/delivery-address/deleteAddress/${encodeURIComponent(addressId)}`,
        token
      );
      return true;
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }

  async handleDeleteDeliveryAddressAndRefresh({
    addressId,
    token,
    selectedAddress,
    setLoading,
    setApiError,
    onAuthError,
    onForbidden,
    refreshAddresses,
    clearSelectedId,
  }: HandleDeleteAndRefreshParams): Promise<void> {
    const deleteSucceeded = await this.handleDeleteDeliveryAddress({
      addressId,
      token,
      selectedAddress,
      setLoading,
      setApiError,
      onAuthError,
      onForbidden,
    });

    if (deleteSucceeded) {
      await refreshAddresses();
      clearSelectedId();
    }
  }

  handleOpenDeleteDeliveryAddress({
    selectedAddress,
    clearError,
    openModal,
  }: IHandleOpenDeleteDeliveryAddressParams): void {
    if (!selectedAddress) return;
    if (selectedAddress.isDefaultAddress) return;

    clearError();
    openModal();
  }

  handleOpenAdd({ isLoggedIn, setOpenAddModal }: HandleOpenAddParams): void {
    if (!isLoggedIn) return;
    setOpenAddModal(true);
  }

  handleCloseAdd({ setOpenAddModal }: HandleCloseAddParams): void {
    setOpenAddModal(false);
  }

  async handleCreated({ refreshAddresses, setOpenAddModal }: HandleCreatedParams): Promise<void> {
    await refreshAddresses();
    setOpenAddModal(false);
  }

  async handleGetCarriers(params: IHandleGetCarriersParams): Promise<void> {
    const { token, setLoading, setApiError, setCarriers, onAuthError, onForbidden } = params;

    setApiError(null);
    setLoading(true);

    try {
      const res = await AxiosCalls.Get<GetDeliveryCarriersResponse>(
        '/delivery-carrier/get-carriers',
        token
      );
      const carriers = res.data.data.items;
      setCarriers(carriers);
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
      setCarriers([]);
    } finally {
      setLoading(false);
    }
  }
  getDefaultCarrier({
    carriers,
    selectedCarrier,
    setSelectedCarrier,
  }: IDefaultCarrierParams): void {
    const availableCarriers = carriers ?? [];

    if (!availableCarriers.length) {
      if (selectedCarrier) setSelectedCarrier(null);
      return;
    }

    const selectedId = selectedCarrier?.id ?? '';

    const isSelectedStillAvailable =
      selectedId !== '' && availableCarriers.some((c) => c.id === selectedId);

    const resolvedCarrier = isSelectedStillAvailable ? selectedCarrier : availableCarriers[0];

    if (resolvedCarrier?.id !== selectedCarrier?.id) {
      setSelectedCarrier(resolvedCarrier);
    }
  }

  selectAddress(params: {
    id: string;
    addresses: IDeliveryAddressView[];
    setSelectedAddress: (a: IDeliveryAddressView | null) => void;
  }): void {
    const id = String(params.id ?? '');
    const found = (params.addresses ?? []).find((a) => a.id === id) ?? null;
    params.setSelectedAddress(found);
  }
}
